import { getCometh4337Wallet } from '$services/cometh/getComethWallet';
import { getMetriAddress } from '$utils/getMetriAddress';
import { type Address, type Call, type Hex } from 'viem';

import { forgeComethPaymasterStubData } from './paymaster';
import {
  type GetPaymasterDataParameters,
  type GetPaymasterDataReturnType,
  type GetPaymasterStubDataReturnType,
  type UserOperation,
} from 'viem/account-abstraction';

import { PUBLIC_USEROP_OPTIMIZED } from '$env/static/public';
import type { createComethPaymasterClient } from '@cometh/connect-sdk-4337';

const createPaymasterConfig = (
  paymasterClient: ReturnType<typeof createComethPaymasterClient>,
) => ({
  getPaymasterStubData: async (
    params: GetPaymasterDataParameters,
  ): Promise<GetPaymasterStubDataReturnType> => {
    return forgeComethPaymasterStubData(params);
  },
  getPaymasterData: async (
    params: GetPaymasterDataParameters,
  ): Promise<GetPaymasterDataReturnType> => {
    return paymasterClient.getPaymasterData(params);
  },
});

export const sendUserOperation = async ({
  userOperation,
  calls,
  address,
  notOptimized,
}: {
  userOperation?: UserOperation;
  calls?: Call[];
  address?: Address;
  notOptimized?: boolean;
}): Promise<Hex> => {
  const resolvedAddress = address ?? getMetriAddress();
  const { smartAccountClient, paymasterClient } = await getCometh4337Wallet(resolvedAddress);

  if (userOperation) {
    const signature = await smartAccountClient.account.signUserOperation(userOperation);
    return smartAccountClient.sendUserOperation({ ...userOperation, signature });
  }

  if (!calls || calls.length === 0) {
    throw new Error('Either userOperation or calls must be provided.');
  }

  const parameters: {
    calls: Call[];
    paymaster?: {
      getPaymasterData: (params: GetPaymasterDataParameters) => Promise<GetPaymasterDataReturnType>;
      getPaymasterStubData: (
        params: GetPaymasterDataParameters,
      ) => Promise<GetPaymasterStubDataReturnType>;
    };
  } = {
    calls,
    // This env enables us to use a little magic to bypass the getPaymasterStubData api call and gain 300ms. In case it fails
    // we want to be able to switch mode quickly, hence the env
    ...(PUBLIC_USEROP_OPTIMIZED &&
      PUBLIC_USEROP_OPTIMIZED === 'true' &&
      !notOptimized && { paymaster: createPaymasterConfig(paymasterClient) }),
  };

  return smartAccountClient.sendUserOperation(parameters);
};

export const prepareUserOperation = async ({
  calls,
  notOptimized,
  address,
}: {
  calls: Call[];
  notOptimized?: boolean;
  address?: Address;
}): Promise<UserOperation> => {
  const addressToUse = address ?? getMetriAddress();
  const { smartAccountClient, paymasterClient } = await getCometh4337Wallet(addressToUse);

  const parameters: {
    calls: Call[];
    paymaster?: {
      getPaymasterData: (params: GetPaymasterDataParameters) => Promise<GetPaymasterDataReturnType>;
      getPaymasterStubData: (
        params: GetPaymasterDataParameters,
      ) => Promise<GetPaymasterStubDataReturnType>;
    };
  } = {
    calls,
    ...(PUBLIC_USEROP_OPTIMIZED &&
      PUBLIC_USEROP_OPTIMIZED === 'true' &&
      !notOptimized && { paymaster: createPaymasterConfig(paymasterClient) }),
  };

  return smartAccountClient.prepareUserOperation(parameters);
};
